<template>
    <div class="upload-container">
        <h3 class="titles">{{ $t('modals.patient_profile.profile.detail_form.upload.head') }}</h3>

        <!-- Upload Box -->
        <div class="upload-Box">
            <div class="upload-icon">
                <common-icon name="chose" size="extra-large" @click="openFilePicker"></common-icon>
            </div>
            <input ref="fileInput" type="file" class="fileInput" multiple @change="handleFileChange" />
            <p class="upload-text">{{ $t('modals.patient_profile.profile.detail_form.upload.drop_text') }}</p>
            <p class="upload-subtext">{{ $t('modals.patient_profile.profile.detail_form.upload.subtext') }}</p>
        </div>

        <!-- Selected Files List -->
        <div v-if="fileUpload.files.length" class="selected-files-wrapper">
            <div v-for="(file, index) in fileUpload.files" :key="index" class="file-pill">
                <span class="file-label">{{ file.name }}</span>
                <button class="close-btn" @click="removeFile(index)">×</button>
            </div>
        </div>
        <p v-if="fileErrorMessage" class="upload-subtext_error">{{ fileErrorMessage }}</p>

        <form-wrapper>
            <form-row>
                <form-cell width="half">
                    <label class="form-field__label">
                        {{ $t('forms.patient_profile.fields.profile.label') }}
                    </label>
                    <select-input
                        class="schedule-apply__input"
                        :placeholder="$t('forms.patient_profile.fields.profile.placeholder')"
                        :hasAutofocus="true"
                        :options="documentOptions"
                        :value="recordTypeId"
                        @change="changeCurrentDocumentId($event)"
                    ></select-input>
                </form-cell>
                <form-cell width="half">
                    <label class="form-field__label">
                        {{ $t('forms.patient_profile.fields.profile.document.label') }}
                    </label>
                    <text-field
                        :placeholder="$t('forms.patient_profile.fields.profile.document.placeholder')"
                        :isRequired="true"
                        type="date"
                        :formData="data"
                        valuePath="date"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell width="half">
                    <label class="form-field__label">
                        {{ $t('forms.patient_profile.fields.profile.provider.label') }}
                    </label>
                    <div :class="searchAutocompleteClass">
                        <div :class="iconClass">
                            <common-icon :name="iconName" size="large"></common-icon>
                        </div>

                        <input
                            v-model="searchTerm"
                            type="text"
                            class="search-autocomplete__input"
                            :placeholder="$t('forms.patient_profile.fields.profile.provider.placeholder')"
                            :disabled="!!selectedUser"
                            @keyup="inputChange"
                            @focus="inputFocus"
                            @blur="inputBlur"
                        />
                        <div v-if="!!items.length" class="search-autocomplete__suggestions">
                            <div v-for="(item, index) in items" :key="index">
                                <single-item
                                    :disableNetworkPlan="disableNetworkPlan"
                                    :item="item"
                                    @make-referral="userSelected($event)"
                                ></single-item>
                            </div>
                        </div>
                        <common-user-label
                            v-if="selectedUser"
                            class="search-autocomplete__user-label"
                            :user="selectedUser"
                            :isDisabled="isDisabled"
                            @close="removeSelectedUser()"
                        ></common-user-label>
                    </div>
                </form-cell>
                <form-cell width="half">
                    <textarea-field
                        :label="$t('forms.patient_profile.fields.profile.comment.label')"
                        :placeholder="$t('forms.patient_profile.fields.profile.comment.placeholder')"
                        :formData="data"
                        valuePath="note"
                        @change="change($event)"
                    ></textarea-field>
                </form-cell>
            </form-row>
        </form-wrapper>

        <!-- Button -->
        <div class="button-row">
            <common-button
                :label="$t('modals.patient_profile.profile.detail_form.upload.uploadbutton.title')"
                size="medium"
                type="primary"
                icon="chose"
                :isProcessing="isUploading"
                :isDisabled="!fileUpload.files.length || isUploading"
                @click="uploadDocuments"
            ></common-button>
        </div>
        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
    </div>
</template>

<script>
    import debounce from 'lodash.debounce'
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'
    import PatientAPI from 'api/PatientAPI'
    import SingleItem from 'components/page-elements/search-autocomplete/SearchProviderAutocompleteSingleItem'
    import config from '../../../config'

    export default {
        mixins: [FormMixin, HandleRequestMixin],

        components: {
            SingleItem,
        },

        props: {
            user: { type: Object, default: null },
            disableNetworkPlan: { type: Boolean, default: false },
            icon: { type: String, default: 'member' },
            iconPosition: { type: String, default: 'left' },
            iconBackground: { type: String, default: 'secondary' },
            isDisabled: { type: Boolean, default: false },
            documentOptions: { type: Array, default: () => [] },
        },

        data() {
            return {
                patientId: null,
                recordTypeId: '',
                searchTerm: '',
                items: [],
                selectedUser: null,
                isFocused: false,
                fileUpload: {
                    files: [],
                },
                notificationMessage: '',
                isUploading: false,
                fileErrorMessage: null,
            }
        },

        computed: {
            searchAutocompleteClass() {
                return {
                    [`search-autocomplete__search-input--${this.iconPosition}`]: true,
                    'search-autocomplete__search-input': true,
                    'form-input-text': true,
                }
            },

            iconName() {
                if (this.iconBackground === 'primary' && this.isFocused) return `${this.icon}-white`
                return `${this.icon}-gray`
            },

            iconClass() {
                return {
                    'search-autocomplete__icon': true,
                    [`search-autocomplete__icon--${this.iconPosition}`]: true,
                    [`search-autocomplete__icon--color-${this.iconBackground}`]: this.isFocused,
                }
            },
        },

        methods: {
            changeCurrentDocumentId(val) {
                this.recordTypeId = val
            },

            change(val) {
                this.data.note = val.value
            },

            inputChange: debounce(function () {
                this.emitEvent({ name: 'input-change' })
                if (this.searchTerm.length >= 2) {
                    PatientAPI.getProviderFromNetwork(this.searchTerm, 5, 0).then((val) => {
                        if (val.length > 0) {
                            val.unshift({ name: this.searchTerm, type: 'typed' })
                            this.items = val
                        } else {
                            this.items = []
                            this.emitEvent({ name: 'no-results', payload: { searchTerm: this.searchTerm } })
                        }
                    })
                } else {
                    this.items = []
                }
            }, config.DEBOUNCE.DEFAULT),

            userSelected(event) {
                if (event?.type === 'typed') return
                this.searchTerm = ''
                this.items = []
                this.selectedUser = event
                this.emitEvent({ name: 'user-select', payload: event })
            },

            removeSelectedUser() {
                this.selectedUser = null
                this.emitEvent({ name: 'user-select', payload: null })
            },

            inputFocus() {
                this.isFocused = true
            },

            inputBlur() {
                this.isFocused = false
            },

            openFilePicker() {
                this.$refs.fileInput.click()
            },

            handleFileChange(event) {
                const selectedFiles = Array.from(event.target.files)

                selectedFiles.forEach((file) => {
                    this.fileUpload.files.push({
                        name: file.name,
                        size: (file.size / (1024 * 1024)).toFixed(1) + ' MB',
                        file: file,
                    })
                })
            },

            removeFile(index) {
                this.fileErrorMessage = ''
                this.fileUpload.files.splice(index, 1)
            },

            getBase64(file) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader()
                    reader.readAsDataURL(file)
                    reader.onload = () => resolve(reader.result)
                    reader.onerror = (error) => reject(error)
                })
            },

            async uploadDocuments() {
                this.fileErrorMessage = ''
                this.isUploading = true
                this.notificationMessage = ''
                if (!this.fileUpload.files.length) {
                    this.isUploading = false
                    return
                }

                let filesArray = []
                for (const f of this.fileUpload.files) {
                    const base64 = await this.getBase64(f.file)

                    filesArray.push({
                        file: base64,
                        fileName: f.name,
                        size: Math.round(f.file.size / 1024),
                    })
                }

                if (filesArray.some((file) => file.size > 5 * 1024)) {
                    this.fileErrorMessage = this.$t('pages.patient.profile.upload_error', {
                        fileName: filesArray.find((f) => f.size > 5 * 1024).fileName,
                    })
                    this.isUploading = false
                    return
                }

                let sourcePracticeId = null
                let sourceUserId = null
                let sourceName = ''

                let fromPracticeId = this.$auth.getUser().practiceId
                const activeMember = this.$auth.getActiveMember()

                if (this.selectedUser) {
                    if (this.selectedUser?.type === 'user') {
                        sourcePracticeId = this.selectedUser.practice?.id
                        sourceUserId = this.selectedUser.id
                    }

                    if (this.selectedUser?.type === 'practice') {
                        sourcePracticeId = this.selectedUser.id
                    }
                } else {
                    sourcePracticeId = activeMember.practice?.id
                    sourceUserId = activeMember.id
                }

                if (this.selectedUser?.name) {
                    sourceName = this.selectedUser.designation
                        ? `${this.selectedUser.name}, ${this.selectedUser.designation}`
                        : this.selectedUser.name
                } else if (this.searchTerm) {
                    sourceName = this.searchTerm
                } else {
                    sourceName = activeMember.designation
                        ? `${activeMember.firstName} ${activeMember.lastName}, ${activeMember.designation}`
                        : `${activeMember.firstName} ${activeMember.lastName}`
                }

                if (!this.recordTypeId) {
                    const otherOption = this.documentOptions.find((opt) => opt.label?.toLowerCase() === 'other')
                    this.recordTypeId = otherOption ? otherOption.value : ''
                }

                const payload = {
                    files: filesArray,
                    patientId: Number(this.patientId),
                    recordTypeId: this.recordTypeId,
                    fromPracticeId,
                    sourceName,
                    date: this.data.date,
                    sourcePracticeId: sourcePracticeId,

                    ...(this.$auth.isPracticeAncillary() ? { fromProviderId: this.$auth.getUser().id } : {}),
                    ...(sourceUserId ? { sourceUserId } : {}),
                    ...(this.data.note ? { note: this.data.note } : {}),
                }
                await PatientAPI.uploadDocuments(payload)
                    .then(() => {
                        this.notificationMessage = this.$t('pages.patient.profile.notifications.upload_success')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                        this.emitEvent({ name: 'documents-uploaded' })
                    })
                    .catch((error) => {
                        if (error.response.data[0].code === 'fileBase64.mimeType') {
                            this.fileErrorMessage = this.$t('pages.patient.profile.upload_error_mime_type')
                        } else {
                            this.fileErrorMessage = this.$t('pages.patient.profile.upload_error_common')
                        }
                    })
                    .finally(() => {
                        this.isUploading = false
                    })
                this.resetForm()
            },

            resetForm() {
                this.fileUpload.files = []
                this.searchTerm = ''
                this.items = []
                this.selectedUser = null
                this.recordTypeId = ''
                this.data.note = ''
            },
        },
        mounted() {
            const today = new Date().toISOString().split('T')[0]
            this.data.date = today

            this.patientId = this.$route.params.id
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';

    .titles {
        font-size: 14px;
        font-weight: 400;
        margin-bottom: 16px;
        text-align: start;
    }

    .upload-Box {
        border: 3px dashed #ccc;
        border-radius: 8px;
        padding: 30px;
        text-align: center;
        margin-bottom: 20px;
        background: #fff;
        cursor: pointer;
        position: relative;
    }

    .upload-icon {
        font-size: 30px;
        color: #00cfe2;
        margin-bottom: 10px;
    }

    .upload-text {
        font-size: 16px;
        color: oklch(0.145 0 0);
        font-weight: 500;
        margin: 0;
    }

    .upload-subtext {
        font-size: 13px;
        color: #777;
        margin-top: 5px;
    }

    .upload-subtext_error {
        color: $color--accent-8;
    }

    .form-field__label {
        font-size: 14px;
        font-weight: bold;
        margin-bottom: 6px;
        text-align: start;
        display: block;
    }

    .button-row {
        text-align: right;
        margin: 15px 0;
    }

    input.fileInput {
        width: 100%;
        opacity: 0;
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
    }

    .selected-files-wrapper {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 15px;
        margin-bottom: 15px;
        background-color: #f5f5f5;
        padding: 10px;
        border-radius: 5px;
    }

    .file-pill {
        background: #13b3e3;
        color: #fff;
        padding: 6px 12px;
        border-radius: 20px;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        border-radius: 4px;
    }

    .file-label {
        white-space: nowrap;
    }

    .close-btn {
        background: transparent;
        border: none;
        color: white;
        font-size: 14px;
        cursor: pointer;
        font-weight: bold;
        padding: 0 4px;
    }

    .search-autocomplete__header-item {
        padding: 12px 16px;
        font-weight: 700;
        font-size: 14px;
        border-bottom: 1px solid #e6e6e6;
        background: #fff;
        cursor: default;
    }
</style>
